import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import type { DiligenceArtifact } from '../types';
import { getArtifactPresentation } from '../utils/documentUiState';

interface DiligenceArtifactCardProps {
  artifact: DiligenceArtifact;
  onDownload?: (artifactId: string, fileName: string) => Promise<void>;
}

const DiligenceArtifactCard: React.FC<DiligenceArtifactCardProps> = ({ artifact, onDownload }) => {
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'error'>('idle');
  const presentation = getArtifactPresentation(artifact);
  const isReviewedDraft = artifact.quality === 'reviewed_draft';
  const hasDeliverableQuality = artifact.quality === 'verified'
    || isReviewedDraft
    || artifact.quality === 'approximate';
  const isReady = artifact.status === 'ready' && hasDeliverableQuality;
  const isPdf = presentation.format === 'PDF';
  const downloading = downloadStatus === 'downloading';

  const handleDownload = async () => {
    if (!onDownload || !isReady || downloading) return;
    setDownloadStatus('downloading');
    try {
      await onDownload(artifact.id, artifact.name);
      setDownloadStatus('idle');
    } catch {
      setDownloadStatus('error');
    }
  };

  return (
    <div className="mb-3 w-full min-w-0 max-w-full sm:w-fit">
      <section
        aria-label={`Documento generado: ${artifact.name}`}
        className={`rounded-2xl border bg-panel/90 p-3 text-ink shadow-sm ${artifact.status === 'failed' ? 'border-danger' : 'border-juris/10'}`}
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-black text-white shadow-sm ${isPdf ? 'bg-danger-action text-[10px] tracking-tight' : 'bg-info-action text-xl'}`}
          >
            {isPdf ? 'PDF' : 'W'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="[overflow-wrap:anywhere] text-sm font-extrabold leading-snug">{artifact.name}</p>
            {artifact.templateName && <p className="mt-2 [overflow-wrap:anywhere] text-xs leading-relaxed text-ink/70">Plantilla: {artifact.templateName}</p>}
          </div>
          {isReady && onDownload && (
            <button type="button" disabled={downloading} onClick={() => void handleDownload()} className="flex size-11 shrink-0 items-center justify-center rounded-full bg-action text-white shadow-sm transition hover:bg-action-hover disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2" aria-label={downloading ? `Preparando ${artifact.name}` : `Descargar ${artifact.name}`} title={downloading ? 'Preparando descarga' : 'Descargar documento'}>
              {downloading
                ? <Loader2 size={17} className="animate-spin" aria-hidden="true" />
                : <Download size={17} aria-hidden="true" />}
            </button>
          )}
        </div>
      </section>
      {downloadStatus === 'error' && (
        <p role="alert" className="mt-2 text-xs font-semibold text-danger">No se pudo preparar la descarga. Inténtalo de nuevo.</p>
      )}
    </div>
  );
};

export default DiligenceArtifactCard;
